import { useCallback, useEffect, useRef, useState } from 'react'
import { isTouch } from 'lib/support'

/**
 * The `useTouchHoverEvents` hook is used to enable horizontal scrolling
 * at certain breakpoints
 *
 * @param {object} ref - the react ref
 * @returns {boolean} whether is hovering
 */
export default function useTouchHoverEvents (ref) {
  const [isHover, setIsHover] = useState(false)
  const lastNode = useRef(null)

  /**
   * On hover start
   */
  const handleHoverStart = useCallback(() => {
    setIsHover(true)
  }, [])

  /**
   * On hover end
   */
  const handleHoverEnd = useCallback(() => {
    setIsHover(false)
  }, [])

  /**
   * Add the handlers
   */
  const addHandlers = useCallback(node => {
    if (!node) {
      return
    }

    if (isTouch) {
      node.addEventListener('touchstart', handleHoverStart)
      node.addEventListener('touchend', handleHoverEnd)
      node.addEventListener('touchcancel', handleHoverEnd)
    }
  }, [])

  /**
   * Remove the handlers
   */
  const removeHandlers = useCallback(node => {
    if (!node) {
      return
    }

    node.removeEventListener('touchstart', handleHoverStart)
    node.removeEventListener('touchend', handleHoverEnd)
    node.removeEventListener('touchcancel', handleHoverEnd)
  }, [])

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    if (lastNode.current) {
      removeHandlers(lastNode.current)
      lastNode.current = null
    }

    const node = ref?.current

    if (!node) {
      return
    }

    lastNode.current = node

    addHandlers(node)

    return () => {
      removeHandlers(node)
    }
  }, [ref])

  return isHover
}
